import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BarChart3, Package, LogOut, Settings, Sliders, Users, Truck, Store } from 'lucide-react';
import AddProduct from '../AddProduct/AddProduct';
import AdminManagement from '../AdminManagement/AdminManagement';
import ManageProducts from '../ManageProducts/ManageProducts';
import ManageHeroSlider from '../ManageHeroSlider/ManageHeroSlider';
import ManageFreeShippingCard from '../ManageFreeShippingCard/ManageFreeShippingCard';
import ManagePickupOrders from '../ManagePickupOrders/ManagePickupOrders';
import './AdminDashboard.css';

function AdminDashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState('analytics');
  const [shouldScrollToSection, setShouldScrollToSection] = useState(false);
  const activeSectionRef = useRef(null);

  async function handleLogout() {
    try {
      await logout();
      navigate('/admin');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  }

  const analyticsData = {
    totalSales: '$12,345',
    totalOrders: '156',
    averageOrder: '$79.13',
    activeUsers: '234'
  };

  useEffect(() => {
    if (!shouldScrollToSection || !activeSectionRef.current) {
      return;
    }

    activeSectionRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    setShouldScrollToSection(false);
  }, [selectedSection, shouldScrollToSection]);

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
    setShouldScrollToSection(true);
  };

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1 className="dashboard-title">Admin Dashboard</h1>
        <div className="header-right">
          <span className="admin-email">{currentUser?.email}</span>
          <button onClick={handleLogout} className="logout-button">
            <LogOut size={18} style={{ marginRight: '0.5rem' }} />
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="menu-tiles">
          <div 
            className={`menu-tile ${selectedSection === 'analytics' ? 'active' : ''}`}
            onClick={() => handleSectionSelect('analytics')}
          >
            <div className="tile-header">
              <div className="tile-icon">
                <BarChart3 size={24} />
              </div>
              <h2 className="tile-title">Site Analytics</h2>
            </div>
            <p className="tile-description">
              View detailed analytics about sales, orders, and user engagement.
            </p>
          </div>

          <div 
            className={`menu-tile ${selectedSection === 'products' ? 'active' : ''}`}
            onClick={() => handleSectionSelect('products')}
          >
            <div className="tile-header">
              <div className="tile-icon">
                <Package size={24} />
              </div>
              <h2 className="tile-title">Add Product</h2>
            </div>
            <p className="tile-description">
              Add new products to your store with details and pricing.
            </p>
          </div>

          <div 
            className={`menu-tile ${selectedSection === 'manage-products' ? 'active' : ''}`}
            onClick={() => handleSectionSelect('manage-products')}
          >
            <div className="tile-header">
              <div className="tile-icon">
                <Settings size={24} />
              </div>
              <h2 className="tile-title">Manage Products</h2>
            </div>
            <p className="tile-description">
              Edit or delete existing products in your store.
            </p>
          </div>

          <div 
            className={`menu-tile ${selectedSection === 'hero-slider' ? 'active' : ''}`}
            onClick={() => handleSectionSelect('hero-slider')}
          >
            <div className="tile-header">
              <div className="tile-icon">
                <Sliders size={24} />
              </div>
              <h2 className="tile-title">Hero Slider</h2>
            </div>
            <p className="tile-description">
              Manage the hero slider images and content.
            </p>
          </div>

          <div 
            className={`menu-tile ${selectedSection === 'free-shipping-card' ? 'active' : ''}`}
            onClick={() => handleSectionSelect('free-shipping-card')}
          >
            <div className="tile-header">
              <div className="tile-icon">
                <Truck size={24} />
              </div>
              <h2 className="tile-title">Homepage Shipping</h2>
            </div>
            <p className="tile-description">
              Configure the homepage shipping promotion card.
            </p>
          </div>

          <div 
            className={`menu-tile ${selectedSection === 'pickup-orders' ? 'active' : ''}`}
            onClick={() => handleSectionSelect('pickup-orders')}
          >
            <div className="tile-header">
              <div className="tile-icon">
                <Store size={24} />
              </div>
              <h2 className="tile-title">Pickup Orders</h2>
            </div>
            <p className="tile-description">
              Track in-store pickup requests and update pickup status.
            </p>
          </div>

          <div 
            className={`menu-tile ${selectedSection === 'admin-users' ? 'active' : ''}`}
            onClick={() => handleSectionSelect('admin-users')}
          >
            <div className="tile-header">
              <div className="tile-icon">
                <Users size={24} />
              </div>
              <h2 className="tile-title">Admin Users</h2>
            </div>
            <p className="tile-description">
              Create new admin accounts and review existing admin users.
            </p>
          </div>
        </div>

        <div className="dashboard-active-section" ref={activeSectionRef}>
          {selectedSection === 'analytics' && (
            <div className="analytics-container">
              <div className="analytics-header">
                <h2 className="analytics-title">Site Analytics Overview</h2>
              </div>
              <div className="analytics-grid">
                <div className="analytics-card">
                  <div className="analytics-value">{analyticsData.totalSales}</div>
                  <div className="analytics-label">Total Sales</div>
                </div>
                <div className="analytics-card">
                  <div className="analytics-value">{analyticsData.totalOrders}</div>
                  <div className="analytics-label">Total Orders</div>
                </div>
                <div className="analytics-card">
                  <div className="analytics-value">{analyticsData.averageOrder}</div>
                  <div className="analytics-label">Average Order Value</div>
                </div>
                <div className="analytics-card">
                  <div className="analytics-value">{analyticsData.activeUsers}</div>
                  <div className="analytics-label">Active Users</div>
                </div>
              </div>
            </div>
          )}

          {selectedSection === 'products' && (
            <div className="analytics-container">
              <div className="analytics-header">
                <h2 className="analytics-title">Add New Product</h2>
              </div>
              <AddProduct />
            </div>
          )}

          {selectedSection === 'manage-products' && (
            <ManageProducts />
          )}

          {selectedSection === 'hero-slider' && (
            <ManageHeroSlider />
          )}

          {selectedSection === 'free-shipping-card' && (
            <div className="analytics-container">
              <ManageFreeShippingCard />
            </div>
          )}

          {selectedSection === 'pickup-orders' && (
            <ManagePickupOrders />
          )}

          {selectedSection === 'admin-users' && (
            <AdminManagement />
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
